<script lang="ts">
  import type { CorrectionsViewModel } from "../lib/corrections";
  import type { DerivedWorkout } from "../lib/lapAnalysis";
  import SetRibbon from "./SetRibbon.svelte";
  import CorrectionReveal from "./CorrectionReveal.svelte";

  let { vm, dw }: { vm: CorrectionsViewModel; dw: DerivedWorkout } = $props();

  const n = $derived(vm.changed.length);

  // Which corrected length's reveal is open, by swim-wide lap index. Tapping the
  // same chip again closes it; tapping another corrected chip switches to it.
  let openLapIndex = $state<number | null>(null);
  const openRec = $derived(vm.changed.find((r) => r.lapIndex === openLapIndex) ?? null);

  let revealEl: HTMLElement | null = $state(null);

  // Persona panel B1: the reveal opened off-screen, below the floating nav
  // pill, and the user never saw the payoff. Bring it into view every time a
  // NEW length opens (not on close, not on re-render of the same one).
  $effect(() => {
    if (openRec && revealEl) revealEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
  });

  function onLapTap(lapIndex: number) {
    openLapIndex = openLapIndex === lapIndex ? null : lapIndex;
  }
</script>

{#if n > 0}
  {#if openLapIndex === null}
    <p class="tap-hint">Tap a marked length to see what changed.</p>
  {/if}
  <SetRibbon
    {dw}
    correctedLapIndices={vm.changedLapIndices}
    {onLapTap}
    selectedLapIndex={openLapIndex}
    correctionMode
  />
  {#if openRec}
    <div class="reveal-clearance" bind:this={revealEl}>
      <CorrectionReveal rec={openRec} />
    </div>
  {/if}
{/if}

{#if vm.unmatchedCount > 0}
  <p class="unmatched">
    {vm.unmatchedCount === 1
      ? "1 saved fix doesn't line up with a length in this swim."
      : `${vm.unmatchedCount} saved fixes don't line up with lengths in this swim.`}
  </p>
{/if}

<style>
  /* Reserve room below the reveal so the floating nav pill (~72px) can never
     cover the from -> to payoff (persona panel B1). Same clearance formula
     used elsewhere for content that can land at the bottom of the scroll. */
  .reveal-clearance {
    padding-bottom: calc(72px + var(--space-3) + env(safe-area-inset-bottom, 0px));
  }
  .unmatched {
    margin: 8px 0 0;
    font-size: 0.85rem;
    opacity: 0.75;
  }
  /* Nothing else on this card said the chips were tappable — discovering the
     reveal was luck (persona panel round 2, 2026-07-12). Plain sentence,
     full-strength ink since it's the instruction for using the whole card. */
  .tap-hint {
    margin: 0 0 var(--space-2);
    font-size: 15px;
    color: var(--text-primary);
  }
</style>
